import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Send, IdCard, Trophy, Crown, Sparkles, Sword, Star, Shield } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  { key: "deshi",   icon: Sword,    points: 80,   color: "text-yellow-400" },
  { key: "padawan", icon: Star,     points: 150,  color: "text-yellow-400" },
  { key: "sensei",  icon: Shield,   points: 280,  color: "text-yellow-400" },
  { key: "hero",    icon: Trophy,   points: 500,  color: "text-yellow-400" },
  { key: "king",    icon: Crown,    points: 800,  color: "text-yellow-400" },
  { key: "god",     icon: Sparkles, points: 1100, color: "text-yellow-400" },
];

export const Newsletter = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/card-requests`, form);
      toast.success(t("newsletter.success"));
      setDone(true);
      setForm({ first_name: "", last_name: "", phone: "", email: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="newsletter" data-testid="newsletter-section" className="relative bg-yellow-400 py-24 overflow-hidden">
      <div className="absolute inset-0 halftone-strong opacity-30" />
      <div className="absolute top-0 left-0 right-0 h-3 diagonal-stripes" />
      <div className="absolute bottom-0 left-0 right-0 h-3 diagonal-stripes" />
      <div className="absolute -top-10 -right-10 font-jp text-black/5 text-[18rem] select-none leading-none">忠</div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center bg-black text-yellow-400 p-4 mb-5 brutal-shadow-black">
            <IdCard className="w-8 h-8" strokeWidth={3} />
          </div>
          <div className="font-jp text-black text-sm tracking-widest mb-3">忠誠 / {t("newsletter.eyebrow")}</div>
          <h2 className="font-heading text-5xl sm:text-7xl uppercase text-black leading-none">
            {t("newsletter.title_1")}<br />
            <span className="bg-black text-yellow-400 px-4 inline-block -skew-x-6 mt-2">{t("newsletter.title_2")}</span>
          </h2>
          <p className="text-black/80 text-lg max-w-3xl mx-auto mt-6 font-semibold">{t("newsletter.subtitle")}</p>
        </div>

        {/* Tiers grid */}
        <div className="mb-12">
          <div className="text-center font-heading uppercase text-black/70 text-xs tracking-widest mb-5">{t("newsletter.tiers_title")}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {TIERS.map((tier, idx) => {
              const Icon = tier.icon;
              return (
                <div
                  key={tier.key}
                  data-testid={`tier-${tier.key}`}
                  className="bg-zinc-950 border-4 border-black p-4 hover:border-yellow-400/40 hover:translate-y-[-4px] transition group relative"
                >
                  <div className="absolute -top-3 -right-3 bg-yellow-400 text-black w-8 h-8 flex items-center justify-center font-heading text-sm border-2 border-black">
                    {idx + 1}
                  </div>
                  <Icon className={`w-7 h-7 ${tier.color} mb-3`} strokeWidth={2.5} />
                  <div className="font-heading uppercase text-white text-base leading-tight">{t(`newsletter.tier_${tier.key}`)}</div>
                  <div className="text-yellow-400 text-xs mt-1 font-bold uppercase tracking-widest">{t(`newsletter.tier_${tier.key}_desc`)}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Form */}
        <div className="bg-zinc-950 border-4 border-black brutal-shadow-black max-w-3xl mx-auto p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-5">
            <IdCard className="w-6 h-6 text-yellow-400" strokeWidth={3} />
            <div>
              <h3 className="font-heading uppercase text-yellow-400 text-2xl leading-none">{t("newsletter.form_title")}</h3>
              <p className="text-zinc-400 text-sm mt-1">{t("newsletter.form_hint")}</p>
            </div>
          </div>

          {done ? (
            <div data-testid="card-success" className="text-center py-6">
              <div className="inline-flex items-center justify-center bg-yellow-400 text-black w-16 h-16 mb-4">
                <Sparkles className="w-8 h-8" strokeWidth={3} />
              </div>
              <div className="font-heading uppercase text-white text-2xl">{t("newsletter.success")}</div>
              <button
                onClick={() => setDone(false)}
                data-testid="card-reset"
                className="mt-5 text-yellow-400 hover:underline text-xs uppercase tracking-widest font-bold"
              >
                ← Nuova richiesta
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="card-form" className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">{t("newsletter.first_name")}</label>
                  <input
                    data-testid="card-first-name"
                    required value={form.first_name} onChange={onChange("first_name")}
                    placeholder={t("newsletter.placeholder_first")}
                    className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-3 text-white focus:border-yellow-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">{t("newsletter.last_name")}</label>
                  <input
                    data-testid="card-last-name"
                    required value={form.last_name} onChange={onChange("last_name")}
                    placeholder={t("newsletter.placeholder_last")}
                    className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-3 text-white focus:border-yellow-400 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">{t("newsletter.phone")}</label>
                  <input
                    data-testid="card-phone"
                    required type="tel" value={form.phone} onChange={onChange("phone")}
                    placeholder={t("newsletter.placeholder_phone")}
                    className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-3 text-white focus:border-yellow-400 focus:outline-none font-mono"
                  />
                </div>
                <div>
                  <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-1 block">Email</label>
                  <input
                    data-testid="card-email"
                    required type="email" value={form.email} onChange={onChange("email")}
                    placeholder={t("newsletter.placeholder_email")}
                    className="w-full bg-zinc-900 border-2 border-zinc-800 px-4 py-3 text-white focus:border-yellow-400 focus:outline-none font-mono"
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="card-submit"
                disabled={loading}
                className="w-full bg-yellow-400 text-black font-heading uppercase tracking-widest text-lg px-8 py-4 border-4 border-yellow-400 hover:bg-white transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? t("newsletter.submitting") : t("newsletter.submit")} <Send className="w-4 h-4" strokeWidth={3} />
              </button>

              <p className="text-center text-zinc-500 text-xs uppercase tracking-widest pt-2">{t("newsletter.disclaimer")}</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
